"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  FormHeader,
  MarkedText,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ACADEMIC_BIO_BANK,
  type AcademicBioInput,
  BIO_LENGTHS,
  type BioLength,
  type BioPronoun,
  type BioVersion,
  buildBios,
  SOCIAL_MAX_CHARACTERS,
} from "@/lib/tool-kit/correspondence/academic-bio";
import {
  countPlaceholders,
  draftToHtml,
  toPlain,
} from "@/lib/tool-kit/correspondence/compose";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const ACADEMIC_BIO_GENERATOR_STRINGS = {
  formHeading: "About you",
  tryExample: "Try an example",
  clear: "Clear",
  nameLabel: "Full name",
  namePlaceholder: "Adaeze Okafor",
  positionLabel: "Position",
  positionPlaceholder: "Associate Professor of Marine Science",
  positionHint:
    "Your title as it would follow “is”. The tool adds “a” or “an” where English needs one.",
  institutionLabel: "Institution",
  institutionPlaceholder: "University of Lagos",
  fieldLabel: "Field (optional)",
  fieldPlaceholder: "marine ecology",
  interestsLabel: "Research interests, separated by commas",
  interestsPlaceholder:
    "coral reef resilience, ocean warming, community-based reef monitoring",
  interestsHint: "Most important first: shorter bios keep the first ones.",
  achievementsLabel: "Achievements, one per line (optional)",
  achievementsPlaceholder:
    "a national early-career research prize (2024)\na five-year fellowship to study reef recovery",
  publicationsLabel: "Selected publications, one per line (optional)",
  publicationsPlaceholder: "“Reef recovery after repeated bleaching” (2025)",
  publicationsHint: "Used on the profile page only.",
  personalLabel: "Personal line (optional)",
  personalPlaceholder: "Outside work, she coaches a youth chess club.",
  personalHint: "One sentence, written in the same person as the bio.",
  pronounLabel: "In the third person, refer to you as",
  pronounOptions: {
    name: "My name (no pronouns)",
    she: "she/her",
    he: "he/him",
    they: "they/them",
  },
  shortNameLabel: "Name for later mentions (optional)",
  shortNamePlaceholder: "Okafor",
  shortNameHint:
    "Used when you choose no pronouns. Leave blank to repeat your full name.",
  lengthLabel: "Length",
  lengthOptions: {
    "50": "About 50 words",
    "100": "About 100 words",
    "150": "About 150 words",
  },
  personLabel: "Person",
  personOptions: {
    third: "Third person",
    first: "First person",
  },
  resultsHeading: "Your bios",
  contextTitles: {
    conference: "Conference speaker bio",
    journal: "Journal author note",
    profile: "University profile page",
    social: "Social media profile",
  },
  contextNotes: {
    conference:
      "Written to be read aloud by a session chair, so it lists no publications.",
    journal:
      "Always in the third person, and never longer than about 100 words.",
    profile:
      "The fullest version: interests, achievements, publications, and the personal line.",
    social:
      "Position, interests, and pronouns in one line, cut to fit short profile fields.",
  },
  wordCount: "one:{count} word|other:{count} words",
  target: "target: about {target}",
  characterCount: "one:{count} character|other:{count} characters",
  overLimit:
    "Longer than {limit} characters: shorten your position or first interest.",
  copy: "Copy",
  copied: "Copied!",
  downloadAll: "Download all as .txt",
  downloadFilename: "academic-bio.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders: "No placeholders left in the bios.",
  example: {
    name: "Adaeze Okafor",
    shortName: "Okafor",
    position: "Associate Professor of Marine Science",
    institution: "University of Lagos",
    field: "marine ecology",
    interests:
      "coral reef resilience, ocean warming, community-based reef monitoring",
    achievements:
      "a national early-career research prize (2024)\na five-year fellowship to study reef recovery in the Gulf of Guinea",
    publications:
      "“Reef recovery after repeated bleaching” (2025)\n“Who counts the fish? Community monitoring on West African reefs” (2023)",
    personal: "Outside work, she coaches a youth chess club in Lagos.",
  },
  ...ACADEMIC_BIO_BANK,
} as const;

type Strings = typeof ACADEMIC_BIO_GENERATOR_STRINGS;

const EMPTY: AcademicBioInput = {
  name: "",
  shortName: "",
  pronoun: "name",
  position: "",
  institution: "",
  field: "",
  interests: "",
  achievements: "",
  publications: "",
  personal: "",
  length: "100",
  person: "third",
};

const PRONOUNS: readonly BioPronoun[] = ["name", "she", "he", "they"];

/** The free-text facts, each drawn as one input. */
type FactKey =
  | "name"
  | "institution"
  | "position"
  | "field"
  | "interests"
  | "achievements"
  | "publications"
  | "personal";

function BioCard({
  version,
  strings,
  locale,
  fmt,
}: {
  version: BioVersion;
  strings: Strings;
  locale: string;
  fmt: (value: number) => string;
}) {
  const social = version.context === "social";
  const count = social
    ? tPlural(strings.characterCount, locale, version.characters, {
        count: fmt(version.characters),
      })
    : tPlural(strings.wordCount, locale, version.words, {
        count: fmt(version.words),
      });
  const detail = social
    ? ""
    : ` · ${t(strings.target, { target: fmt(version.target) })}`;
  return (
    <article className={`${RESULT_CARD} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={WIDGET_H3}>{strings.contextTitles[version.context]}</h3>
        <CopyButton
          copiedLabel={strings.copied}
          html={draftToHtml([version.text])}
          label={strings.copy}
          small
          text={toPlain(version.text)}
        />
      </div>
      <p className="break-words text-foreground text-sm leading-relaxed">
        <MarkedText text={version.text} />
      </p>
      <p className="font-medium text-foreground text-xs">
        {count}
        {detail}
      </p>
      {social && version.characters > SOCIAL_MAX_CHARACTERS ? (
        <p className={HINT}>
          {t(strings.overLimit, { limit: fmt(SOCIAL_MAX_CHARACTERS) })}
        </p>
      ) : null}
      <p className={HINT}>{strings.contextNotes[version.context]}</p>
    </article>
  );
}

function FactsFields({
  form,
  update,
  strings,
}: {
  form: AcademicBioInput;
  update: <K extends keyof AcademicBioInput>(
    key: K,
    value: AcademicBioInput[K]
  ) => void;
  strings: Strings;
}) {
  const text = (key: FactKey) => ({
    id: `abg-${key}`,
    onChange: (value: string) => update(key, value),
    value: form[key],
  });
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          {...text("name")}
          label={strings.nameLabel}
          placeholder={strings.namePlaceholder}
        />
        <TextField
          {...text("institution")}
          label={strings.institutionLabel}
          placeholder={strings.institutionPlaceholder}
        />
        <TextField
          {...text("position")}
          hint={strings.positionHint}
          label={strings.positionLabel}
          placeholder={strings.positionPlaceholder}
        />
        <TextField
          {...text("field")}
          label={strings.fieldLabel}
          placeholder={strings.fieldPlaceholder}
        />
      </div>
      <TextField
        {...text("interests")}
        hint={strings.interestsHint}
        label={strings.interestsLabel}
        placeholder={strings.interestsPlaceholder}
      />
      <CompactTextArea
        {...text("achievements")}
        label={strings.achievementsLabel}
        placeholder={strings.achievementsPlaceholder}
        rows={2}
      />
      <CompactTextArea
        {...text("publications")}
        hint={strings.publicationsHint}
        label={strings.publicationsLabel}
        placeholder={strings.publicationsPlaceholder}
        rows={2}
      />
      <TextField
        {...text("personal")}
        hint={strings.personalHint}
        label={strings.personalLabel}
        placeholder={strings.personalPlaceholder}
      />
    </>
  );
}

export default function AcademicBioGeneratorWidget() {
  const strings = useToolUi(ACADEMIC_BIO_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<AcademicBioInput>(EMPTY);

  const update = <K extends keyof AcademicBioInput>(
    key: K,
    value: AcademicBioInput[K]
  ) => setForm((current) => ({ ...current, [key]: value }));

  const versions = useMemo(
    () => buildBios(form, strings, locale),
    [form, strings, locale]
  );
  const blanks = countPlaceholders(versions.map((version) => version.text));
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;

  function download() {
    const text = versions
      .map(
        (version) =>
          `${strings.contextTitles[version.context]}\n\n${toPlain(version.text)}`
      )
      .join("\n\n\n");
    downloadText(strings.downloadFilename, text);
  }

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY)}
          onExample={() =>
            setForm({ ...EMPTY, ...strings.example, pronoun: "she" })
          }
        />
        <FactsFields form={form} strings={strings} update={update} />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="abg-pronoun"
            label={strings.pronounLabel}
            onChange={(value: BioPronoun) => update("pronoun", value)}
            options={PRONOUNS.map((key) => ({
              value: key,
              label: strings.pronounOptions[key],
            }))}
            value={form.pronoun}
          />
          <TextField
            hint={strings.shortNameHint}
            id="abg-short-name"
            label={strings.shortNameLabel}
            onChange={(value) => update("shortName", value)}
            placeholder={strings.shortNamePlaceholder}
            value={form.shortName}
          />
        </div>
        <PillGroup
          label={strings.lengthLabel}
          name="abg-length"
          onChange={(value: BioLength) => update("length", value)}
          options={BIO_LENGTHS.map((key) => ({
            value: key,
            label: strings.lengthOptions[key],
          }))}
          value={form.length}
        />
        <PillGroup
          label={strings.personLabel}
          name="abg-person"
          onChange={(value) => update("person", value)}
          options={[
            { value: "third", label: strings.personOptions.third },
            { value: "first", label: strings.personOptions.first },
          ]}
          value={form.person}
        />
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          <button className={BUTTON_SECONDARY} onClick={download} type="button">
            {strings.downloadAll}
          </button>
        </div>
        <p aria-live="polite" className={HINT}>
          {status}
        </p>
        <div className="grid gap-4">
          {versions.map((version) => (
            <BioCard
              fmt={fmt}
              key={version.context}
              locale={locale}
              strings={strings}
              version={version}
            />
          ))}
        </div>
      </section>
    </>
  );
}
